import React, { useState } from "react";
import { useOpenModal } from "#utils/openModal";
import { useMemoizedFn, useRequest } from "ahooks";
import { localPx2rem } from "#utils";
import { ProTablePlus } from "@bedrock/eed-react";
import { IProTableRef } from "@bedrock/eed-react/lib/components/ProTable/interface";
import { FilterType } from "@bedrock/eed-react/lib/components/ProFilter/interface";
import { demandRightsListCategoryTree, demandRightsPageContentList } from "#services/api";
import { queryDemandRightsPageContentList } from "./util";
import { $sType } from "@mpc/common";
import styles from './index.less'
import classNames from "classnames/bind";

const cx = classNames.bind(styles);

const useAddEquityModal = ({
  onOk,
  rowKey = 'rights_code',
  allRights = false,
}) => {
  const tableRef = React.useRef<IProTableRef<any>>();
  // 选中集合显式声明为对象数组：`disabledRows` / `selectRows` 会参与「已存在权益」判定与合并，
  // 隐式的 `never[]`（useState([]) 的推断）会让合并结果无法回写。
  const [disabledRows, setDisabledRows] = useState<Record<string, any>[]>([]);
  const [selectRows, setSelectedRows] = useState<Record<string, any>[]>([]);
  const [rightsList, setRightsList] = useState([]);
  const {data: typeOptions} = useRequest(
    () => demandRightsListCategoryTree({}),
    {ready: $sType === 'inner'}
  );
  
  const columns = [
    {
      title: '权益内容',
      dataIndex: 'content',
      width: localPx2rem(342),
      className: 'fw-bold'
    },
    {
      title: '类别',
      dataIndex: 'category_name',
      width: localPx2rem(160),
    },
    {
      title: '类别',
      dataIndex: 'category_ids',
      hideInTable: true,
      searchRender: {
        showCondition: false,
        type: FilterType.SELECT_MULTIPLE,
        filterComponentProps: {
          options: typeOptions?.data?.tree,
          fieldNames: {
            label: 'name',
            value: 'id',
            children: 'children_demand_rights_category_list'
          }
        },
      }
    },
  ]

  const queryService = async (query) => {
    const params = query?.params;
    const { current, pageSize: limit, content_fuzzy, paramsWithConditions } = params;
    const conditions = Object.entries(paramsWithConditions).reduce((pre, [key, value]: [string, any]) => {
        pre[key] = value.value
      return pre
    }, {})
    try {
      const request = allRights ? demandRightsPageContentList : queryDemandRightsPageContentList
      const res = await request({
        current,
        limit,
        status: 1,
        ...conditions,
        content_fuzzy,
      }, rightsList)

      return {
        data: {
          list: res.data?.list?.map?.(v => ({
            ...v,
            rights_code: v.code,
            rights_id: v.id,
          })),
          total: res.data?.total_count,
        }
      }
    } catch (error) {
      console.log('error', error);
      return { data: { list: [], total: 0 } }
    }
  }

  /**
   * 该权益是否**已存在**于当前子数组（必要/期望权益）。
   *
   * 已存在权益不能用 `rowSelection.disabledRowKeys` 表达：bedrock 会**给整行加禁用底色**，
   * 与「已有权益的行保持白色背景、不用整行底色区分」冲突 → 只把该行 checkbox 置灰。
   */
  const isExistingRight = (row: Record<string, any>) =>
    disabledRows.some((d: Record<string, any>) => d[rowKey] === row[rowKey]);

  /**
   * 已有权益行的「强制白底」类。
   *
   * 这类行同时是**选中态**（checkbox 勾选且禁用），表格会按选中态给整行铺一层浅蓝底，与
   * 「保持白色背景」冲突。底色必须落在 `td` 上才盖得住 —— bedrock 的行底色选择器是
   * `tr.…-selected > td`，只写 `tr` 的背景会被 `td` 自己的背景盖住。
   * 本仓 Tailwind 配置 `important: true`，故该工具类自带 `!important`，可稳定覆盖组件样式。
   */
  const EXISTING_RIGHT_ROW_CLASS = '[&>td]:bg-white';

  /** 选中集合恒含「已存在权益」——它们 checkbox 恒选中且禁用，不能被全选/反选冲掉 */
  const mergeExistingRows = (rows: Record<string, any>[]) => {
    const next = [...disabledRows];
    rows.forEach((r) => {
      if (!next.some((v: Record<string, any>) => v[rowKey] === r[rowKey])) next.push(r);
    });
    return next;
  };

  /** 行点击选中/取消（整行可点，不限于第一列 checkbox） */
  const toggleRow = (row: Record<string, any>) => {
    setSelectedRows((prev) => {
      if (prev.some((v) => v[rowKey] === row[rowKey])) {
        return prev.filter((v) => v[rowKey] !== row[rowKey]);
      }
      return [...prev, row];
    });
  };


  const { open } = useOpenModal({
    title: '关联权益',
    width: localPx2rem(960),
    children: (<div className={cx('body-container')}>
      <ProTablePlus
        rowKey={rowKey}
        ref={tableRef}
        request={{ queryService }}
        columns={columns}
        pagination={{ pageSize: 10 }}
        toolbar={{
          filterSetting: {
            disableAddButton: true,
          },
          actionsInTitle: true,
          search: {
            placeholder: '请输入权益内容',
            id: 'content_fuzzy',
          },
        }}
        /**
         * 整行点击选中。用表格级 `onRow` 而非 `rowSelection.onRow`：
         * 前者是本仓 ProTablePlus 既有写法（见 AddItemsModal「onRow 接管行点击选中」注释），
         * 后者是否被 eed 透传未经实测。
         *
         * ⚠️ 必须放行 checkbox 自身区域：点 checkbox 会先触发 `onSelect`，若再冒泡到行点击
         * 就会「选中 → 立刻取消」。
         */
        onRow={(row: Record<string, any>) => ({
          onClick: (e: React.MouseEvent) => {
            if ((e.target as HTMLElement)?.closest?.('.rock-checkbox')) return;
            if (isExistingRight(row)) return;
            toggleRow(row);
          },
          style: { cursor: isExistingRight(row) ? 'default' : 'pointer' },
        })}
        /** 已有权益行强制白底（不用整行底色区分，只靠 checkbox 表达状态） */
        rowClassName={(row: Record<string, any>) => (isExistingRight(row) ? EXISTING_RIGHT_ROW_CLASS : '')}
        rowSelection={{
          selectedRowKeys: selectRows?.map?.(v => v[rowKey]),
          type: 'checkbox',
          // 已存在权益：只禁用 checkbox，不加整行底色
          getCheckboxProps: (row: Record<string, any>) => ({
            disabled: isExistingRight(row),
          }),
          onSelectAll: (_selected, selectedRows) => {
            setSelectedRows(mergeExistingRows(selectedRows));
          },
          onSelect: (row, selected) => {
            setSelectedRows((prev) => {
              if (prev.some(v => v[rowKey] === row[rowKey])) {
                return prev.filter((v) => v[rowKey] !== row[rowKey])
              }
              return [...prev, row]
            });
          },
        }}
      />
    </div>),
    onOk: () => onOk(selectRows.filter(v => !disabledRows.some(d => d[rowKey] === v[rowKey])))
  });

  return useMemoizedFn(({
    disabledRows,
    rightsList,
  }) => {
    setRightsList(rightsList);
    setDisabledRows(disabledRows);
    setSelectedRows(disabledRows);
    setTimeout(() => {
      open();
    }, 0)
  })
}

export default useAddEquityModal;